<template>
  <div
    class="tm-progress"
    :class="{
      'tm-progress--extra-rounded': extraRounded,
      'tm-progress--bottom': bottom,
      'tm-progress--top-rounded': topRounded,
      'tm-progress--dark-background': darkBg,
    }"
  >
    <tm-tooltip
      v-if="hasTooltip || tooltipText"
      :model-value="tooltipState"
      max-width="250px"
      white
    >
      <div class="tm-progress__tooltip">
        <slot name="tooltip">
          {{ tooltipText }}
        </slot>
      </div>
    </tm-tooltip>
    <div
      ref="track"
      class="tm-progress__track"
      :style="{
        height: size,
        color: color,
      }"
    >
      <div
        ref="bar"
        class="tm-progress__bar"
        :style="{
          width: value * 100 + '%',
        }"
      >
        <div
          ref="barText"
          class="tm-progress__text"
          :class="{
            'tm-progress__text--right': textRight,
            'tm-progress__text--transition': isRender,
          }"
        >
          <slot>
            <div
              v-if="showProgress"
              class="white--text d-flex justify-end flex-grow-1 align-center caption-12"
            >
              <div>{{ value * 100 + '%' }}</div>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from '@/composition/vue/compositionApi'
import TmTooltip from '@/components/shared/tooltip/TmTooltip.vue'

export default defineComponent({
  name: 'TmProgress',
  components: {
    TmTooltip,
  },
  props: {
    size: {
      type: String,
      default: '9px',
    },
    color: {
      type: String,
    },
    value: {
      type: Number,
      default: 0,
    },
    extraRounded: {
      type: Boolean,
      default: false,
    },
    topRounded: {
      type: Boolean,
    },
    bottom: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
    },
    tooltipState: {
      type: Boolean,
      default: false,
    },
    showProgress: {
      type: Boolean,
      default: false,
    },
    darkBg: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const hasTooltip = !!context.slots.tooltip
    const hasText = !!context.slots.default
    const track = ref()
    const barText = ref()
    const textRight = ref(false)
    const isRender = ref(false)

    onMounted(async () => {
      if ((hasText || props.showProgress) && barText.value.clientWidth > track.value.clientWidth * props.value)
        textRight.value = true

      await new Promise((resolve) => {
        setTimeout(resolve, 0)
      })
      isRender.value = true
    })

    watch(
      () => props.value,
      (val) => {
        if ((hasText || props.showProgress) && barText.value.clientWidth > track.value.clientWidth * val) {
          textRight.value = true
        } else {
          textRight.value = false
        }
      },
    )

    return {
      hasTooltip,
      hasText,
      track,
      barText,
      textRight,
      isRender,
    }
  },
})
</script>

<style lang="scss" scoped>
.tm-progress {
  overflow: visible;
  background: $input;
  position: relative;
  border-radius: $border-radius;
  color: $blue;

  &--extra-rounded {
    border-radius: 16px;
  }
  &--dark-background {
    background: $gray3;
  }

  &--bottom {
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    &:not(.tm-progress--top-rounded) {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  &__text {
    padding: 0 8px;
    white-space: nowrap;
    color: $white;
    font-weight: $medium-font;
    font-family: $mono-font-family;
    font-size: $sm-font;

    &--right {
      transform: translateX(100%);

      :deep(*) {
        color: $neutral !important;
      }
    }

    &--transition {
      transition: all $transition-300ms-ease-in-out;
    }
  }

  &__track {
    border-radius: inherit;
  }

  &__bar {
    background: currentColor;
    height: 100%;
    position: absolute;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    left: 0;
    border-radius: inherit;
    top: 0;
    transition: width $transition-300ms-ease-in-out;

    &::before {
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      transition: all $transition-300ms-ease-in-out;
      background: $white;
      opacity: 0;
    }
  }

  &__tooltip {
    color: $neutral;
    padding: 8px 0;
    font-weight: $semi-bold-font;
    font-size: $sm-font;
  }

  &:hover .tm-progress__bar::before {
    opacity: 0.2;
  }
}
</style>
